import { $on } from '../../y/events';
import { yResolve } from '../../y/util';
import { $el } from '../elements';
import { UIMenu, UIMenuItem, uiShowMenu } from '../menu';
import { uiShowPopup } from '../popup';
import { UIClassesSpec, UIRelPos } from '../types';
import { UIView, UIViewSpec } from '../view';
import { UIButton } from './button';
import { UISelect } from './select';

export interface UIToolbarOptions {
    classes?: UIClassesSpec;
}

function makeToolbarButton(item: UIMenuItem, toolbar: UIViewSpec, menu: UIMenu): UIViewSpec {
    if(yResolve(item.when) === false || item.header != undefined || item.widget != undefined)
        return $el.DIV('ui-toolbar-placeholder');

    if(item.separator) {
        return $el.DIV('ui-toolbar-separator');
    } else if(item.space != undefined) {
        return $el.S(item.space);
    } else if(item.inline) {
        return new UIToolbar(item.inline, { classes: 'm-inline' });
    } else if(item.select) {
        const select = new UISelect(item.select, { kind: 'k-toolbar', classes: 'm-hug', no_focus: true, display: item.display });
        select.value = item.value;
        $on(select, 'value', v => {
            item.value = v;
            menu.update(item, toolbar);
            item.changed?.(item.value);
        });
        return select;
    } else {
        const button = new UIButton({ icon: item.icon, title: item.icon ? undefined : yResolve(item.title), kind: 'k-toolbar', link: item.link, prevent_blur: true });

        if('checked' in item) {
            button.classList.add('m-check');
            button.classList.toggle('f-checked', item.checked);

            button.on_click = () => {
                item.checked = !item.checked;
                const new_value = item.checked;
                button.classList.toggle('f-checked', new_value);
                item.changed?.(new_value);
                menu.update(item, toolbar)
            }
        } else if(item.popup) {
            button.on_click = () => {
                const popup = item.popup();
                if(popup instanceof Promise) {
                    popup.then(p => {
                        uiShowPopup(p, button, { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, can_flip: true });
                    });
                } else {
                    uiShowPopup(popup, button, { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, can_flip: true });
                }
            }
        } else if(item.submenu) {
            button.on_click = () => {
                const submenu = yResolve(item.submenu);
                if(submenu instanceof Promise) {
                    submenu.then(submenu => { uiShowMenu(submenu, button) });
                } else {
                    uiShowMenu(submenu, button);
                }
            }
        } else {
            button.on_click = () => {
                item.action?.();
                menu.on_option?.(item);
            }
        }

        return button;
    }
}

export class UIToolbar extends UIView {
    private _menu: UIMenu;
    private _map!: WeakMap<UIMenuItem, UIViewSpec>

    constructor(menu: UIMenu, options: UIToolbarOptions = {}) {
        super('div', ['ui-toolbar', options.classes]);

        menu.addListener(this, (item?: UIMenuItem) => { item ? this._update(item) : this._render() });
        this._menu = menu;
        this._render();

        this.root.addEventListener('mousedown', ev => {
            ev.preventDefault();
            ev.stopPropagation();
        });
    }

    private _render() {
        const map = this._map = new WeakMap();
        const menu = this._menu;

        this.ui_empty();

        if(!menu.hidden) {
            for(const item of menu.items) {
                const view = makeToolbarButton(item, this, menu);
                map.set(item, view);
                this.ui_add(view);
            }
        }
    }

    private _update(item: UIMenuItem) {
        const map = this._map;
        const old = map.get(item);
        if(!old) return;

        const view = makeToolbarButton(item, this, this._menu);
        old.ui_replace(view);
        map.set(item, view);
    }
}
